// src/components/AppShell.tsx
// Seitenleiste für alle eingeloggten Seiten – helles Design (Design-Tokens aus src/design/tokens.ts,
// seit 18.10.2026 statt der alten dunklen Palette).

import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Home as HomeIcon,
  Building2,
  Factory,
  Scale,
  Wallet,
  Calculator,
  Percent,
  Landmark,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Settings,
  Chrome,
  GitCompare,
  Plus,
  Menu,
  X,
} from "lucide-react";
import { SignedIn, UserButton, useClerk, useUser } from "@clerk/clerk-react";
import { useUserPlan } from "@/hooks/useUserPlan";
import { usePortfolio, type PortfolioObject } from "@/hooks/usePortfolio";
import { useCollapsedSection } from "@/hooks/useCollapsedSection";
import { trackDealFlowActionClicked } from "@/hooks/useTrackingEvents";
import { useDealDisplayInfos } from "@/hooks/useDealDisplayInfos";
import { DEAL_SCORE_BADGES } from "@/core/score/dealScoreBadges";
import { buildDealHeading } from "@/lib/dealNaming";
import { colors, font } from "@/design/tokens";
import { openConsentBanner } from "@/components/analytics/ConsentBanner";

/* -- Helle Design-Tokens ---------------------------------------------------- */
const BG_SIDEBAR = colors.white;
const BG_SIDEBAR_HOVER = colors.sageSoft;
const BG_ACTIVE = colors.sageSoft;
const BORDER = colors.line;
const TEXT_MUTED = colors.muted;
const TEXT_DIM = colors.ink;
const AKZENT = colors.forest;

// Nachtrag 15.10.2026 (Punkt 4, "Mobil: Seitenleiste als ausklappbares Menü"): unter 768px
// wird die permanente Spalte (.pp-appshell-aside) ausgeblendet, der Hamburger-Button
// (seit 20.10.2026 in der Kopfleiste .pp-appshell-topbar) sichtbar -- per CSS statt einer JS-Breakpoint-Abfrage, da hier
// kein Re-Render bei Resize noetig ist (das ausgeklappte Menue selbst ist reiner React-
// State, siehe AppShell()).
// Nachtest 16.10.2026 (Dominiks Live-Befund, beim Mobil-390-Screenshot der Objektseite
// entdeckt): ".pp-appshell-aside { display: none; }" griff live NIE -- derselbe
// CSS-Spezifitaetsfehler wie bei den Tab-Unterstrichen (design/components/Tabs.tsx):
// das <aside> setzt display:"flex" zusaetzlich als Inline-Style, und eine
// Inline-style-Eigenschaft gewinnt IMMER gegen jede Stylesheet-Regel, auch eine
// @media-Regel, ausser die Stylesheet-Regel traegt !important (wie es die
// Hamburger-Regel hier schon tat -- nur die aside-Regel hatte es vergessen). Die
// Seitenleiste blieb dadurch auf JEDER Bildschirmbreite in voller fixer Breite sichtbar
// und quetschte den Seiteninhalt auf schmalen Bildschirmen in eine kaum noch lesbare
// schmale Spalte.
const MOBILE_SIDEBAR_CSS = `
.pp-appshell-aside { height: 100vh; height: 100dvh; }
/* Sicherheitsnetz gegen seitliches Wischen am Handy (Dominiks Befund 20.10.2026): clip statt
   hidden, damit main kein Scroll-Container wird (sticky Tabs in DealDetail greifen weiter). */
.pp-appshell-main { overflow-x: clip; }
@media (max-width: 767px) {
  .pp-appshell-aside { display: none !important; }
  .pp-appshell-topbar { display: flex !important; }
  :root { --pp-sidebar-width: 0px !important; }
}

/* Nachtrag 17.10.2026 (Baustein 5, Mobil-Fix Zahlenfelder): die 2/3-spaltigen
   Zahlenfeld-Grids (MoneyInput/NumberField/PercentField) in den 5 Analyzer-
   Formularen (hell + dunkel) sind auf echten Handy-Breiten (~390px) zu schmal,
   unfokussierte Werte werden am rechten Rand abgeschnitten. Inline-style
   gridTemplateColumns gewinnt gegen eine normale Media-Query-Regel, deshalb
   !important (gleiches Muster wie .pp-appshell-aside oben). Betrifft nur
   Grids, die explizit die Klasse tragen -- Kennzahlen-/Vergleichskarten
   bleiben unveraendert. */
@media (max-width: 480px) {
  .pp-num-grid-2, .pp-num-grid-3 { grid-template-columns: 1fr !important; }
}
`;

/* -- Nav Items -------------------------------------------------------------
   Jedes Tool ist für jeden eingeloggten Nutzer voll zugänglich (binäres
   FREE/PRO gilt auf Feature-, nicht auf Tool-Ebene) -- mit drei bewussten
   Ausnahmen (Objekt-Vergleich, Abschreibungs-Planer, Chrome-Erweiterung),
   die komplett PRO-only sind. Nur diese drei tragen requiredPlan/ein Badge. */
type NavItem = {
  href: string;
  label: string;
  icon: React.ReactNode;
  requiredPlan?: "pro";
};

// Nachtrag Phase-4 Runde 2a ("Meine Deals" ins neue Design, Punkt 7): die 5 Analyzer
// standen bisher in einer eigenen "Analyzer"-Sektion ÜBER "Meine Deals" -- Dominiks
// Vorgabe dreht die Reihenfolge um ("Neues Objekt prüfen" -> "Meine Deals" -> "Werkzeuge"),
// die 5 Analyzer wandern dafür in die (standardmäßig eingeklappte) "Werkzeuge"-Sektion,
// direkt vor die bisherigen TOOL_ITEMS -- EINE Liste statt zwei getrennter Abschnitte.

// "Meine Deals" ist ab Phase-4 Runde 2a eine eigene, dynamische Sektion (MeineDealsSection
// unten) statt eines statischen Links hier -- deshalb nicht mehr Teil von TOOL_ITEMS.
// Baustein 6: Die 5 Analyzer stehen nicht mehr unter „Werkzeuge“ -- neue Objekte starten über
// „Neues Objekt prüfen“ (/check), gespeicherte Objekte öffnen die Objektseite.
const TOOL_ITEMS: NavItem[] = [
  { href: "/finanzierung-simpel", label: "Finanzierungsrechner", icon: <Calculator size={16} /> },
  { href: "/finanzierungsvergleich", label: "Finanzierungsvergleich", icon: <GitCompare size={16} /> },
  { href: "/miete", label: "Miet-Kalkulator", icon: <Wallet size={16} /> },
  { href: "/vergleich", label: "Objekt-Vergleich", icon: <Scale size={16} />, requiredPlan: "pro" },
  { href: "/afa", label: "Abschreibungs-Planer", icon: <Percent size={16} />, requiredPlan: "pro" },
];

/** Nachtrag 15.10.2026 (Seitenleiste, Punkt 2): kompakte Kaufpreis-Anzeige fuer die zweite
 *  Zeile unter jedem Deal-Namen ("1,2 Mio € · 99093") -- ab 1 Mio. in "Mio €" (eine
 *  Nachkommastelle, nur wenn nicht glatt), sonst der volle Euro-Betrag ohne Nachkommastellen. */
function formatKaufpreisCompact(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return "";
  if (v >= 1_000_000) {
    const mio = v / 1_000_000;
    const decimals = Math.abs(mio - Math.round(mio)) < 0.05 ? 0 : 1;
    return `${mio.toLocaleString("de-DE", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} Mio €`;
  }
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
}

/* -- Sidebar Item ----------------------------------------------------------- */
function SidebarItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation();
  const isActive = location.pathname === item.href;

  return (
    <NavLink
      to={item.href}
      title={collapsed ? item.label : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: collapsed ? "8px 12px" : "8px 10px",
        borderRadius: 8,
        marginBottom: 2,
        fontSize: 13,
        fontWeight: isActive ? 600 : 400,
        color: isActive ? AKZENT : TEXT_DIM,
        background: isActive ? BG_ACTIVE : "transparent",
        border: "1px solid transparent",
        textDecoration: "none",
        transition: "all 0.15s",
        justifyContent: collapsed ? "center" : "flex-start",
        whiteSpace: "nowrap",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        if (!isActive) (e.currentTarget as HTMLElement).style.background = BG_SIDEBAR_HOVER;
      }}
      onMouseLeave={(e) => {
        if (!isActive) (e.currentTarget as HTMLElement).style.background = "transparent";
      }}
    >
      <span style={{ flexShrink: 0, opacity: isActive ? 1 : 0.7 }}>{item.icon}</span>
      {!collapsed && (
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>
      )}
      {!collapsed && item.requiredPlan === "pro" && (
        <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", color: colors.white, background: AKZENT, borderRadius: 100, padding: "1.5px 6px" }}>PRO</span>
      )}
    </NavLink>
  );
}

/* -- Sidebar Section Label ---------------------------------------------- */
function SectionLabel({ label, collapsed }: { label: string; collapsed: boolean }) {
  if (collapsed) return <div style={{ height: 1, background: BORDER, margin: "10px 8px" }} />;
  return (
    <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: TEXT_MUTED, padding: "12px 10px 6px" }}>
      {label}
    </div>
  );
}

/* -- "Meine Deals" (Phase-4 Runde 2a) --------------------------------------
   "Neues Objekt prüfen"-Button oben, darunter die letzten 5 Deals (Titel +
   Score), "Alle Deals" darunter. Score generisch fuer alle 5 Objektarten
   berechnet (computeDealResultFromSnapshot(), dieselbe Funktion wie
   DealDetail.tsx/Check.tsx) -- anders als Portfolio.tsx's Kartenliste, die
   bisher nur fuer ETW live einen Score zeigt. Die eigentliche Aufloesungslogik
   steht in src/lib/dealSidebarDisplay.ts (Nachtest 15.10.2026, Punkt 2e: dort
   direkt mit tests/deal-sidebar-score-parity.test.mjs gegen DealDetail.tsx/
   Check.tsx auf Paritaet geprueft -- diese Komponente bleibt duenn).

   Nachtrag 15.10.2026 (Dominiks Vorgabe, Punkte 1/2): "Alle Deals" klappt die Liste jetzt
   auf (statt zu einem Link zu werden), Zustand wie "Werkzeuge" in localStorage
   (useCollapsedSection), Beschriftung "Alle Deals (N)" <-> "Weniger anzeigen". Ein eigener
   "Übersicht"-Link fuehrt weiterhin zu /deals. Ist der aktuell per Route geoeffnete Deal
   (activeDealId) nicht unter den letzten 5, wird die Liste zusaetzlich (nur fuer dieses
   Rendering, ohne den gespeicherten Zustand zu aendern) erzwungen aufgeklappt -- sonst
   waere der hervorgehobene Deal in der Seitenleiste gar nicht sichtbar. */
function MeineDealsSection({ collapsed, activeDealId }: { collapsed: boolean; activeDealId: string | null }) {
  const navigate = useNavigate();
  const { objects, loading } = usePortfolio();
  const { collapsed: listCollapsed, setCollapsed: setListCollapsed } = useCollapsedSection("propora.sidebarAlleDealsCollapsed", true);

  const top5Ids = new Set(objects.slice(0, 5).map((d) => d.id));
  const mustForceExpand = activeDealId != null && !top5Ids.has(activeDealId) && objects.some((d) => d.id === activeDealId);
  const expanded = !listCollapsed || mustForceExpand;
  const visibleDeals = expanded ? objects : objects.slice(0, 5);
  const display = useDealDisplayInfos(visibleDeals);

  function openDeal(deal: PortfolioObject) {
    trackDealFlowActionClicked("sidebar", "sidebar_deal_opened", deal.id, deal.analyzer_type);
    navigate(`/deal/${deal.id}`);
  }
  function startNewCheck() {
    trackDealFlowActionClicked("sidebar", "sidebar_new_check", "", "");
    navigate("/check?neu=1");
  }

  const newCheckButton = (
    <button
      onClick={startNewCheck}
      title={collapsed ? "Neues Objekt prüfen" : undefined}
      style={{
        display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 8,
        width: "100%", padding: collapsed ? "8px 12px" : "8px 10px", marginBottom: 6, borderRadius: 8,
        background: colors.lime, border: "1px solid transparent", color: AKZENT,
        fontSize: 13, fontWeight: 600, cursor: "pointer",
      }}
    >
      <Plus size={16} />
      {!collapsed && <span>Neues Objekt prüfen</span>}
    </button>
  );

  if (collapsed) {
    return (
      <>
        <SectionLabel label="Meine Objekte" collapsed={collapsed} />
        {newCheckButton}
        <SidebarItem item={{ href: "/deals", label: "Meine Objekte", icon: <TrendingUp size={16} /> }} collapsed={collapsed} />
      </>
    );
  }

  return (
    <div>
      <SectionLabel label="Meine Objekte" collapsed={collapsed} />
      {newCheckButton}
      {!loading && objects.length === 0 ? (
        <div style={{ fontSize: 12, color: TEXT_MUTED, padding: "8px 10px", lineHeight: 1.5 }}>
          Noch kein Objekt – starte mit dem Schnell-Check.
        </div>
      ) : (
        <>
          <div style={expanded ? { maxHeight: 320, overflowY: "auto" } : undefined}>
            {visibleDeals.map((deal) => {
              const info = display[deal.id];
              const heading = buildDealHeading({ name: deal.name, objektart: deal.analyzer_type, ort: info?.ort ?? null });
              const isActive = deal.id === activeDealId;
              const kaufpreisCompact = deal.kaufpreis ? formatKaufpreisCompact(deal.kaufpreis) : null;
              const plz = info?.plz ?? deal.plz ?? null;
              const subtitle = [kaufpreisCompact, plz].filter(Boolean).join(" · ");
              return (
                <button
                  key={deal.id}
                  onClick={() => openDeal(deal)}
                  aria-current={isActive ? "true" : undefined}
                  style={{
                    display: "flex", flexDirection: "column", gap: 1, width: "100%",
                    padding: "7px 10px", marginBottom: 2, borderRadius: 8,
                    background: isActive ? BG_ACTIVE : "transparent",
                    border: "1px solid transparent", fontWeight: isActive ? 600 : 400,
                    color: isActive ? AKZENT : TEXT_DIM, fontSize: 12.5, textAlign: "left", cursor: "pointer",
                  }}
                  onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = BG_SIDEBAR_HOVER; }}
                  onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, width: "100%" }}>
                    {/* Nachtrag 15.10.2026 (Punkt 2): umbricht jetzt statt abzuschneiden
                        (max. 2 Zeilen, danach Ellipsis) -- gleichnamige Deals waren sonst
                        nicht unterscheidbar. */}
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      {heading}
                    </span>
                    {info && (
                      <span style={{
                        flexShrink: 0, fontSize: 10.5, fontWeight: 700, padding: "1.5px 7px", borderRadius: 999,
                        background: DEAL_SCORE_BADGES[info.urteil].background, color: DEAL_SCORE_BADGES[info.urteil].text,
                      }}>
                        {Math.round(info.score)}
                      </span>
                    )}
                  </div>
                  {subtitle && (
                    <span style={{ fontSize: 10.5, color: TEXT_MUTED }}>{subtitle}</span>
                  )}
                </button>
              );
            })}
          </div>

          {objects.length > 5 && (
            <button
              onClick={() => setListCollapsed(!listCollapsed)}
              style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 12, color: TEXT_MUTED, padding: "6px 10px 2px" }}
            >
              {expanded ? "Weniger anzeigen" : `Alle Objekte (${objects.length})`}
            </button>
          )}
          <NavLink
            to="/deals"
            style={{ display: "block", fontSize: 12, color: TEXT_MUTED, padding: "2px 10px 2px", textDecoration: "none" }}
          >
            Übersicht →
          </NavLink>
        </>
      )}
    </div>
  );
}

/* -- Sidebar-Inhalt (Logo/Collapse-Toggle, Nav, Nutzer) -----------------------
   Nachtrag 15.10.2026 (Punkt 4, Mobil-Menue): aus AppShell() ausgelagert, damit die
   permanente Desktop-Spalte und das mobile Overlay-Menue denselben Inhalt rendern, ohne
   ihn zu duplizieren. `onToggleCollapse` (Desktop: Icon-Leiste ein-/ausklappen) und
   `onClose` (Mobil: Overlay schliessen) sind exklusiv -- nur einer der beiden Buttons wird
   gezeigt. */
function SidebarBody({
  collapsed, activeDealId, werkzeugeCollapsed, setWerkzeugeCollapsed, planLabel, fullName,
  onToggleCollapse, onClose,
}: {
  collapsed: boolean;
  activeDealId: string | null;
  werkzeugeCollapsed: boolean;
  setWerkzeugeCollapsed: (v: boolean) => void;
  planLabel: string;
  fullName: string;
  onToggleCollapse?: () => void;
  onClose?: () => void;
}) {
  return (
    <>
      {/* Logo */}
      <div style={{ padding: collapsed ? "16px 10px" : "16px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between", gap: 8 }}>
        {!collapsed && (
          <NavLink to="/" style={{ textDecoration: "none" }}>
            <img src="/assets/propora-logo-navy.png" alt="PROPORA" width={113} height={17} style={{ display: "block", height: 17, width: "auto" }} />
            <div style={{ fontSize: 10, color: TEXT_MUTED, marginTop: 4, letterSpacing: "0.08em", textTransform: "uppercase" }}>Immobilien prüfen</div>
          </NavLink>
        )}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            style={{ background: colors.white, border: `1px solid ${BORDER}`, borderRadius: 6, padding: 4, cursor: "pointer", color: TEXT_MUTED, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        )}
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Menü schließen"
            style={{ background: colors.white, border: `1px solid ${BORDER}`, borderRadius: 6, padding: 4, cursor: "pointer", color: TEXT_MUTED, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: "auto", padding: collapsed ? "8px 4px" : "8px", scrollbarWidth: "none" }}>
        <MeineDealsSection collapsed={collapsed} activeDealId={activeDealId} />

        {collapsed ? (
          <>
            <SectionLabel label="Werkzeuge" collapsed={collapsed} />
            {TOOL_ITEMS.map((item) => (
              <SidebarItem key={item.href} item={item} collapsed={collapsed} />
            ))}
            <SidebarItem key="/extension" item={{ href: "/extension", label: "Chrome-Erweiterung", icon: <Chrome size={16} />, requiredPlan: "pro" }} collapsed={collapsed} />
          </>
        ) : (
          <>
            <button
              onClick={() => setWerkzeugeCollapsed(!werkzeugeCollapsed)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
                background: "none", border: "none", cursor: "pointer", padding: "12px 10px 6px",
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: TEXT_MUTED }}>
                Werkzeuge
              </span>
              <ChevronDown size={14} color={TEXT_MUTED} style={{ transform: werkzeugeCollapsed ? "rotate(-90deg)" : "none", transition: "transform 0.15s" }} />
            </button>
            {!werkzeugeCollapsed && (
              <>
                {TOOL_ITEMS.map((item) => (
                  <SidebarItem key={item.href} item={item} collapsed={collapsed} />
                ))}
                <SidebarItem key="/extension" item={{ href: "/extension", label: "Chrome-Erweiterung", icon: <Chrome size={16} />, requiredPlan: "pro" }} collapsed={collapsed} />
              </>
            )}
          </>
        )}
      </nav>

      {/* User */}
      <div data-testid="sidebar-fuss" style={{ flexShrink: 0, borderTop: `1px solid ${BORDER}`, padding: collapsed ? "12px 8px" : "12px 14px", paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
        <SignedIn>
          {/* afterSignOutUrl auf UserButton selbst ist @deprecated -- in main.tsx
              bereits global auf ClerkProvider gesetzt, Prop hier war redundant. */}
          {collapsed ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <UserButton />
              <AbmeldenButton nurIcon />
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <UserButton />
              <div style={{ overflow: "hidden", flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: TEXT_DIM, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fullName}</div>
                <div style={{ fontSize: 10, color: planLabel === "PRO" ? AKZENT : TEXT_MUTED, fontWeight: 700 }}>{planLabel}</div>
              </div>
              <AbmeldenButton />
            </div>
          )}
          {!collapsed && planLabel !== "PRO" && (
            <NavLink
              to="/upgrade?from=Seitenleiste"
              data-testid="sidebar-upgrade"
              style={{ display: "block", marginTop: 10, padding: "8px 10px", borderRadius: 8, background: colors.sageSoft, color: AKZENT, fontSize: 12, fontWeight: 600, textDecoration: "none", lineHeight: 1.4 }}
            >
              PRO: alle Hebel, Bankbericht & KI-Einordnung →
            </NavLink>
          )}
          {!collapsed && (
            <NavLink to="/account" style={{ display: "inline-block", marginTop: 10, marginRight: 12, fontSize: 11, color: TEXT_MUTED, textDecoration: "underline" }}>
              Konto & Abo
            </NavLink>
          )}
          {!collapsed && (
            <button type="button" onClick={openConsentBanner} style={{ marginTop: 8, background: "none", border: "none", padding: 0, fontSize: 11, color: TEXT_MUTED, cursor: "pointer", textDecoration: "underline" }}>
              Cookie-Einstellungen
            </button>
          )}
        </SignedIn>
      </div>
    </>
  );
}

/* -- Abmelden ------------------------------------------------------------- */
// Dominiks Befund 18.10.2026: Klick auf den Clerk-Avatar öffnete bei ihm (Chrome, mit
// Erweiterungen) kein Menü – damit gab es keinen Weg zum Abmelden. Deshalb ein eigener,
// immer sichtbarer Abmelden-Knopf, unabhängig vom Clerk-Menü. Gleiches Verhalten wie
// routes/auth/Logout.tsx: auch bei Fehler in signOut() sichtbar zu /login.
export function AbmeldenButton({ nurIcon }: { nurIcon?: boolean }) {
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const [laeuft, setLaeuft] = useState(false);
  return (
    <button
      type="button"
      data-testid="sidebar-abmelden"
      title="Abmelden"
      aria-label="Abmelden"
      disabled={laeuft}
      onClick={async () => {
        setLaeuft(true);
        try { await signOut(); } catch { /* trotzdem weiter */ } finally { navigate("/login", { replace: true }); }
      }}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0,
        background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 8,
        padding: nurIcon ? 6 : "6px 10px", color: TEXT_MUTED, cursor: laeuft ? "wait" : "pointer", fontSize: 12,
      }}
    >
      <LogOut size={14} />
      {!nurIcon && "Abmelden"}
    </button>
  );
}

/* -- AppShell ------------------------------------------------------------- */
export default function AppShell({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user } = useUser();
  const { plan } = useUserPlan();
  // Phase-4 Runde 2a: "Werkzeuge" (vormals "Tools") ist jetzt einklappbar, Zustand pro
  // Nutzer in localStorage (useCollapsedSection, try/catch-geschuetzt), standardmaessig
  // eingeklappt (Dominiks Vorgabe).
  const { collapsed: werkzeugeCollapsed, setCollapsed: setWerkzeugeCollapsed } = useCollapsedSection("propora.sidebarWerkzeugeCollapsed", true);

  // Nachtrag 15.10.2026 (Punkt 4): das mobile Overlay-Menue schliesst sich automatisch bei
  // jedem Routenwechsel (unabhaengig davon, welcher Link/Button geklickt wurde).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const planLabel = plan === "pro" ? "PRO" : user ? "FREE" : "GAST";
  const fullName = user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() : "Gast";

  const sidebarWidth = collapsed ? 56 : 304;

  // Formular-Review Punkt 5 (Dominiks Vorgabe): eigene feste Fusszeilen (z. B. "Annahmen
  // pruefen", MFHCheck.tsx) sollen rechts der Seitenleiste beginnen statt sie zu ueberdecken
  // -- die Breite steht als CSS-Variable zur Verfuegung, auf Mobil (Seitenleiste ausgeblendet)
  // per !important auf 0px ueberschrieben (siehe MOBILE_SIDEBAR_CSS unten).
  useEffect(() => {
    document.documentElement.style.setProperty("--pp-sidebar-width", `${sidebarWidth}px`);
  }, [sidebarWidth]);

  // Nachtrag 15.10.2026 (Punkt 4, Deal-Akte in der AppShell): der aktuell geoeffnete Deal
  // (/deal/:id) wird in der Seitenleiste hervorgehoben -- siehe MeineDealsSection.
  const dealMatch = location.pathname.match(/^\/deal\/([^/]+)/);
  const activeDealId = dealMatch ? dealMatch[1] : null;

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: colors.paper, fontFamily: font.family }}>
      <style>{MOBILE_SIDEBAR_CSS}</style>

      {/* -- Sidebar (Desktop/Tablet) -- */}
      <aside
        className="pp-appshell-aside"
        style={{
          width: sidebarWidth,
          flexShrink: 0,
          background: BG_SIDEBAR,
          borderRight: `1px solid ${BORDER}`,
          display: "flex",
          flexDirection: "column",
          transition: "width 0.2s ease",
          position: "sticky",
          top: 0,
          // Höhe über .pp-appshell-aside (100dvh): 100vh ist in Browsern mit ein-/ausblendender
          // Leiste (Safari, Chrome mobil/Tablet) höher als der sichtbare Bereich, der Fuß mit
          // „Abmelden“ lag dann unter der Browserleiste (Dominiks Befund 20.10.2026).
          overflow: "hidden",
        }}
      >
        <SidebarBody
          collapsed={collapsed}
          activeDealId={activeDealId}
          werkzeugeCollapsed={werkzeugeCollapsed}
          setWerkzeugeCollapsed={setWerkzeugeCollapsed}
          planLabel={planLabel}
          fullName={fullName}
          onToggleCollapse={() => setCollapsed((v) => !v)}
        />
      </aside>

      {/* -- Ausgeklapptes Menue (Mobil) -- */}
      {mobileOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex" }}>
          <div onClick={() => setMobileOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(23,61,48,0.35)" }} />
          <aside style={{ position: "relative", width: 280, maxWidth: "85vw", height: "100%", background: BG_SIDEBAR, borderRight: `1px solid ${BORDER}`, display: "flex", flexDirection: "column" }}>
            <SidebarBody
              collapsed={false}
              activeDealId={activeDealId}
              werkzeugeCollapsed={werkzeugeCollapsed}
              setWerkzeugeCollapsed={setWerkzeugeCollapsed}
              planLabel={planLabel}
              fullName={fullName}
              onClose={() => setMobileOpen(false)}
            />
          </aside>
        </div>
      )}

      {/* -- Main Content -- */}
      {/* Nachtest 16.10.2026 (Dominiks Live-Befund, Punkt C): overflow:"auto" machte main
          technisch zu einem eigenen Scroll-Container (CSS-Spec-Definition), der aber in der
          Praxis NIE selbst scrollt -- der aeussere Flex-Container hat nur minHeight:100vh
          (nicht height), main waechst also ungebunden mit, und tatsaechlich scrollt immer
          das Dokument/body (bestaetigt per page.evaluate: mainScrollTop blieb 0,
          documentElement.scrollTop aenderte sich). Das faengt position:sticky-Kindelemente
          (hier: die Tabs-Leiste in DealDetail.tsx) in einem Scroll-Kontext, der sich nie
          bewegt -- sticky griff dadurch nie. overflow entfernt: main war nie wirklich ein
          Scroll-Container, nur die Spec-Klassifizierung stimmte nicht mit dem echten
          Scrollverhalten ueberein. */}
      <main className="pp-appshell-main" style={{ flex: 1, minWidth: 0 }}>
        {/* Kopfleiste nur am Handy (unter 768px, MOBILE_SIDEBAR_CSS): Menü + Logo. Vorher lag
            ein fest positionierter Menü-Knopf über Logo und Überschriften der Seiten. */}
        <div
          className="pp-appshell-topbar"
          style={{ display: "none", alignItems: "center", gap: 12, height: 56, padding: "0 12px", background: BG_SIDEBAR, borderBottom: `1px solid ${BORDER}` }}
        >
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Menü öffnen"
            data-testid="appshell-menue"
            style={{
              display: "flex", width: 40, height: 40, borderRadius: 8, background: colors.white, border: `1px solid ${BORDER}`,
              color: TEXT_DIM, alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer",
            }}
          >
            <Menu size={18} />
          </button>
          <NavLink to="/deals" aria-label="Meine Objekte" style={{ display: "flex" }}>
            <img src="/assets/propora-logo-navy.png" alt="PROPORA" width={107} height={16} style={{ display: "block", height: 16, width: "auto" }} />
          </NavLink>
        </div>
        {children}
      </main>
    </div>
  );
}
